// src/features/ownerAdmin/useCreatorData.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../lib/api';

export interface CommandStatRow {
  command: string;
  total: string;
  failures: string;
  avg_ms: string | null;
}

export interface CreatorStats {
  bot: { total: number; topCmds: CommandStatRow[] };
  website: { total: number; topRoutes: CommandStatRow[] };
  errors: { command: string; source: string; total: string }[];
  rateLimits: { source: string; total: string }[];
  reports: { status: string; total: string }[];
}

export interface GuildStatRow {
  guild_id: string;
  guild_name: string | null;
  removed_at: string | null;
  total: string;
  failures: string;
}

export type ReportStatus = 'unresolved' | 'resolved' | 'skipped' | 'needs_info';

export interface BugReport {
  id: number;
  source: 'bot' | 'website';
  category: string;
  description: string;
  screenshot: string | null;
  guild_id: string | null;
  status: ReportStatus;
  creator_note: string | null;
  submitted_at: string;
  updated_at: string;
}

export function useUnlockStatus() {
  return useQuery({
    queryKey: ['creator-unlock-status'],
    queryFn: () => apiFetch<{ unlocked: boolean; remainingMs: number }>('/api/creator/unlock-status'),
  });
}

export function useUnlock() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (passcode: string) =>
      apiFetch<{ ok: boolean }>('/api/creator/unlock', { method: 'POST', body: JSON.stringify({ passcode }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['creator-unlock-status'] }),
  });
}

export interface CreatorLogEntry {
  id: number;
  guild_id: string | null;
  guild_name: string | null;
  user_id: string;
  user_display_name: string | null;
  action_type: string;
  target_type: string | null;
  target_id: string | null;
  before_value: unknown;
  after_value: unknown;
  created_at: string;
}

export interface CreatorAuditLogEntry {
  id: number;
  creator_id: string;
  action_type: string;
  target_guild_id: string | null;
  target_type: string | null;
  target_id: string | null;
  details: unknown;
  created_at: string;
}

export type LogSort = 'newest' | 'oldest' | 'guild';

export function useAllGuildLogs(sort: LogSort, offset: number, limit = 50, guildId?: string | null, search?: string) {
  return useQuery({
    queryKey: ['creator-all-logs', sort, offset, limit, guildId, search],
    queryFn: () => {
      const params = new URLSearchParams({ sort, limit: String(limit), offset: String(offset) });
      if (guildId) params.set('guildId', guildId);
      if (search) params.set('search', search);
      return apiFetch<{ entries: CreatorLogEntry[]; total: number }>(`/api/creator/logs?${params}`);
    },
  });
}

export function useDeleteLogEntry() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => apiFetch(`/api/creator/logs/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['creator-all-logs'] }),
  });
}

export function useClearAllLogs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<{ deletedCount: number }>('/api/creator/logs', { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['creator-all-logs'] }),
  });
}

export function useCreatorAuditLogs(offset: number, limit = 50) {
  return useQuery({
    queryKey: ['creator-audit-logs', offset, limit],
    queryFn: () => apiFetch<{ entries: CreatorAuditLogEntry[]; total: number }>(`/api/creator/creator-logs?limit=${limit}&offset=${offset}`),
  });
}

export interface FeatureFlag {
  flag_name: string;
  enabled: boolean;
  updated_at: string;
}

export function useFeatureFlags() {
  return useQuery({
    queryKey: ['feature-flags'],
    queryFn: () => apiFetch<FeatureFlag[]>('/api/creator/feature-flags'),
  });
}

export function useToggleFeatureFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ flagName, enabled }: { flagName: string; enabled: boolean }) =>
      apiFetch(`/api/creator/feature-flags/${flagName}`, { method: 'PATCH', body: JSON.stringify({ enabled }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['feature-flags'] }),
  });
}

export function useResetGuildActivity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (guildId: string) => apiFetch(`/api/creator/guilds/${guildId}/reset-activity`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['creator-servers'] }),
  });
}

export interface ServerRow {
  guild_id: string;
  guild_name: string | null;
  removed_at: string | null;
  owner_discord_id: string | null;
  owner_display_name: string | null;
  last_activity_at: string | null;
  joined_at: string;
  effective_last_seen: string;
  inactivity_leave_days_override: number | null;
  has_inactivity_override: boolean;
}

export function useServers() {
  return useQuery({
    queryKey: ['creator-servers'],
    queryFn: () => apiFetch<ServerRow[]>('/api/creator/servers'),
  });
}

export function useSetInactivitySettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ guildId, inactivityLeaveDays }: { guildId: string; inactivityLeaveDays: number | null }) =>
      apiFetch(`/api/creator/servers/${guildId}/inactivity-settings`, {
        method: 'PATCH', body: JSON.stringify({ inactivityLeaveDays }),
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['creator-servers'] }),
  });
}

export function useRemoveBotFromServer() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (guildId: string) => apiFetch(`/api/creator/servers/${guildId}/remove-bot`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['creator-servers'] }),
  });
}

export function useSendBroadcast() {
  return useMutation({
    mutationFn: (message: string) => apiFetch('/api/creator/broadcast', { method: 'POST', body: JSON.stringify({ message }) }),
  });
}

export function useRestartDashboard() {
  return useMutation({
    mutationFn: () => apiFetch('/api/creator/restart-dashboard', { method: 'POST' }),
  });
}

export interface MarketSweepStatus {
  status: 'idle' | 'running';
  currentPosition: number;
  totalItems: number;
  hoursBetweenSweeps: number;
  startedAt: string | null;
  finishedAt: string | null;
  itemsProcessed: number;
  itemsFailed: number;
}

export function useMarketSweepStatus() {
  return useQuery({
    queryKey: ['market-sweep-status'],
    queryFn: () => apiFetch<MarketSweepStatus>('/api/creator/market-sweep-status'),
    // A sweep in progress is worth refreshing periodically without the
    // user needing to manually reload the page to see progress.
    refetchInterval: 30_000,
  });
}

export function useUpdateSweepFrequency() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (hours: number) =>
      apiFetch('/api/creator/market-sweep-frequency', { method: 'PATCH', body: JSON.stringify({ hours }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['market-sweep-status'] }),
  });
}

export function useLeaderboardCacheHours() {
  return useQuery({
    queryKey: ['leaderboard-cache-hours'],
    queryFn: () => apiFetch<{ hours: number }>('/api/creator/leaderboard-cache-hours'),
  });
}

export function useUpdateLeaderboardCacheHours() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (hours: number) =>
      apiFetch('/api/creator/leaderboard-cache-hours', { method: 'PATCH', body: JSON.stringify({ hours }) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leaderboard-cache-hours'] }),
  });
}

export function useCreatorStats() {
  return useQuery({
    queryKey: ['creator-stats'],
    queryFn: () => apiFetch<CreatorStats>('/api/creator/stats'),
  });
}

export function useGuildStats() {
  return useQuery({
    queryKey: ['creator-guild-stats'],
    queryFn: () => apiFetch<GuildStatRow[]>('/api/creator/guild-stats'),
  });
}

export function useBugReports(status: ReportStatus | 'all') {
  return useQuery({
    queryKey: ['creator-reports', status],
    queryFn: () => apiFetch<{ total: number; reports: BugReport[] }>(`/api/creator/reports?status=${status}`),
  });
}

export function useUpdateReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: { id: number; status?: ReportStatus; creator_note?: string }) =>
      apiFetch(`/api/creator/reports/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['creator-reports'] }),
  });
}
